import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  Grid,
  Stack,
  Skeleton,
  CustomPalette,
  PaletteColor,
  CustomTheme,
  useTheme,
  Paper,
} from "@mui/material";
import FilterList from "@mui/icons-material/FilterList";
import WarningIcon from "@mui/icons-material/Warning";
import { toLocaleDateTimeString } from "@utils";
import {
  DASH_EMPTY_FIELD,
  orderStatusColourMapping,
  OrderStatus,
  orderStatusMapping,
} from "@enums";
import { FilterSelect } from "@components";
import { useMemo, useState, useCallback, useEffect } from "react";
import {
  OrderOut,
  OrderStatus as OrderStatusType,
  useWebApiV1ListOrdersQuery,
} from "@providers/hop-ord-server/api";

const DEFAULT_CHECKED = Object.values(orderStatusMapping).filter(
  (value: string) => value != orderStatusMapping[OrderStatus.COMPLETED],
);

const RenderStatusChip = ({
  status,
  isLatest,
}: {
  status: OrderStatusType;
  isLatest?: boolean;
}) => {
  const theme = useTheme<CustomTheme>();
  const color = theme.palette[
    orderStatusColourMapping[status] as keyof CustomPalette
  ] as PaletteColor;
  return (
    <Stack gap={0.5} sx={{ minWidth: 0 }}>
      <Chip
        label={orderStatusMapping[status]}
        data-testid="status-chip-label"
        sx={{
          backgroundColor: color ? color.main : "default",
          color: color ? color.contrastText : "#000000",
          width: "fit-content",
        }}
        variant="filled"
      />
      {!isLatest && (
        <Stack direction="row" gap={0.5}>
          <WarningIcon color="warning" fontSize="small" />
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.warning.dark,
            }}
          >
            RO unsubmitted changes
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

const RenderPatientDetails = ({
  patientName,
  patientDob,
  patientMrn,
}: {
  patientName: string | null;
  patientDob: string | null;
  patientMrn: string | null;
}) => {
  let name_and_dob = "-";
  if (patientName) {
    name_and_dob = patientName + " | DOB: -";
    if (patientDob) {
      name_and_dob = name_and_dob
        .slice(0, -1)
        .concat(toLocaleDateTimeString(patientDob, "date"));
    }
  }
  return (
    <Grid container direction="column">
      <Typography variant="inherit">{name_and_dob}</Typography>
      <Typography variant="inherit" color="grey.700">
        Pat ID: {patientMrn || DASH_EMPTY_FIELD}
      </Typography>
    </Grid>
  );
};

const OrderListItemSkeleton = () => (
  <TableRow>
    <TableCell>
      <Skeleton variant="text" width={1} />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={1} />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={1} />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={1} />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={1} />
    </TableCell>
  </TableRow>
);

const LoadingOverlay = () => (
  <TableBody>
    {[...Array(5).keys()].map((n) => (
      <OrderListItemSkeleton key={n} />
    ))}
  </TableBody>
);

const NoRowsOverlay = () => (
  <TableRow>
    <TableCell colSpan={5} align="center">
      <Typography variant="inherit" pt={1} pl={1} color="grey.700">
        Orders will be displayed here once created
      </Typography>
    </TableCell>
  </TableRow>
);

export const DataTable = ({
  items,
  handleRowClick,
  isLoading,
  statusDefaultFilters,
}: {
  items: OrderOut[];
  handleRowClick: (orderId: number, status: OrderStatusType) => void;
  isLoading: boolean;
  statusDefaultFilters: string[];
}): JSX.Element => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [filteredOrders, setFilteredOrders] = useState<OrderOut[]>([]);

  const filters = useMemo(() => {
    return statusDefaultFilters.map(
      (status) => orderStatusMapping[status as OrderStatusType],
    );
  }, [statusDefaultFilters]);

  const [selectedFilters, setSelectedFilters] =
    useState<string[]>(DEFAULT_CHECKED);

  const orders = useMemo(() => {
    if (!items) return [];
    return items.map((item: OrderOut) => ({
      ...item,
      orderName: item.orderName || DASH_EMPTY_FIELD,
      submittedDatetime: item.submittedDatetime
        ? toLocaleDateTimeString(item.submittedDatetime)
        : DASH_EMPTY_FIELD,
    }));
  }, [items]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const updateFilteredOrders = useCallback(
    (filters: string[]) => {
      setFilteredOrders(
        orders.filter((ord) =>
          filters.includes(orderStatusMapping[ord.status]),
        ),
      );
    },
    [orders],
  );

  useEffect(() => {
    updateFilteredOrders(selectedFilters);
  }, [orders, updateFilteredOrders, selectedFilters, filters]);

  const handleRowClickInternal = (order: OrderOut) => {
    handleRowClick(order.id, order.status);
  };

  return (
    <Stack height="70vh" alignItems="center" width={1}>
      <Stack
        direction="row"
        justifyContent="space-between"
        width={1}
        padding={1}
      >
        <FilterSelect
          label="Status"
          options={filters.map((filter: string) => ({
            label: filter,
            value: filter,
          }))}
          startIcon={<FilterList />}
          setSelectedOptions={setSelectedFilters}
          selectedOptions={selectedFilters}
        />
      </Stack>
      <TableContainer
        component={Paper}
        sx={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Patient</TableCell>
              <TableCell>Order ID</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted Date</TableCell>
            </TableRow>
          </TableHead>
          {isLoading ? (
            <LoadingOverlay />
          ) : filteredOrders.length === 0 ? (
            <NoRowsOverlay />
          ) : (
            <TableBody>
              {filteredOrders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((order) => (
                  <TableRow
                    key={order.id}
                    hover
                    onClick={() => handleRowClickInternal(order)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <RenderPatientDetails
                        patientName={order.patientName}
                        patientDob={order.patientDob}
                        patientMrn={order.patientMrn}
                      />
                    </TableCell>
                    <TableCell>{order.orderGroupId}</TableCell>
                    <TableCell>{order.orderName}</TableCell>
                    <TableCell>
                      <RenderStatusChip
                        status={order.status}
                        isLatest={order.isLatest}
                      />
                    </TableCell>
                    <TableCell>{order.submittedDatetime}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          )}
        </Table>
        <TablePagination
          rowsPerPageOptions={[8]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: "1px solid rgba(224, 224, 224, 1)", mt: "auto" }}
        />
      </TableContainer>
    </Stack>
  );
};

interface Props {
  handleOrderSelect: (orderId: number, status: OrderStatusType) => void;
  statusDefaultFilters: string[];
  recent: boolean;
}

const OrderTable = ({
  handleOrderSelect,
  statusDefaultFilters,
  recent,
}: Props): JSX.Element => {
  const { data, isLoading, isError } = useWebApiV1ListOrdersQuery({
    limit: 20,
    recent,
  });
  if (isError) return <div>Error</div>;
  return (
    <DataTable
      items={data?.items || []}
      isLoading={isLoading}
      handleRowClick={handleOrderSelect}
      statusDefaultFilters={statusDefaultFilters}
    />
  );
};

export default OrderTable;
